import { useLocation } from 'react-router-dom';

import { useAuthentication } from '@zenbusiness/application-commons-authentication';
import {
  ClickstreamProvider,
  ClickstreamScene,
  useClickstream
} from '@zenbusiness/application-commons-clickstream';
import { useEnv } from '@zenbusiness/application-commons-env';
import { LOADING_STATUS, useRegisterLoadingState } from '@zenbusiness/application-commons-loading';
import { useCaptureException, ZBError } from '@zenbusiness/application-commons-logging';

import { NAME } from '#client/build-env';
import { getAppSection } from '#client/utils/getAppSection';

export default function ClickstreamWrapper({ children, ...delegated }) {
  const { obo } = useAuthentication();
  const { DEPLOY_ENV, RUDDERSTACK_API_KEY, RUDDERSTACK_API_URL } = useEnv('head-first'); // use MFE values
  const { pathname } = useLocation();

  const section = getAppSection(pathname);
  const systemProperties = {
    channel: 'web',
    displayTheme: 'light'
  };

  return (
    <ClickstreamProvider
      deployEnv={DEPLOY_ENV}
      noop={!!obo}
      rudderstackApiKey={RUDDERSTACK_API_KEY}
      rudderstackApiUrl={RUDDERSTACK_API_URL}
      {...delegated}
      systemProperties={systemProperties}
    >
      <ClickstreamReady />
      <ClickstreamScene
        appOrigin={NAME}
        appSection={section}
        experienceOrigin="dashboard"
      >
        {children}
      </ClickstreamScene>
    </ClickstreamProvider>
  );
}

function ClickstreamReady() {
  const captureException = useCaptureException();
  let isInitialized = false;

  try {
    // v5.x requires explicit error handling for useClickstream usage
    const clickstream = useClickstream();
    isInitialized = clickstream.ready;
  } catch (error) {
    // Gracefully handle any clickstream initialization errors
    const zbError = new ZBError(error, {
      level: 'warning'
    });
    captureException(zbError, {
      tags: {
        context: 'ClickstreamReady',
        operation: 'clickstream_initialization_check'
      }
    });
    isInitialized = false;
  }

  useRegisterLoadingState({
    loadId: 'Clickstream',
    status: isInitialized ? LOADING_STATUS.DONE : LOADING_STATUS.PENDING
  });
}
